import { useMemo } from "react";
import { Checkbox, List, ListItemButton, ListItemIcon, ListItemText, Paper, Radio, Stack, Typography, useTheme, Button } from "@mui/material"
import { useNavigate } from "react-router-dom";
import EmptyState from "./EmptyState";
import { DINING_MODES, MENU_FLOWS } from "@/constants/global"
import { HugeiconsIcon } from "@hugeicons/react"
import { useAppSelector, useTerminology } from "@/hooks";
import { Branch } from "@/types/branch";

interface Props {
    value?: string | string[];
    onChange: (services: string[]) => void
    multiple?: boolean;
}

const ServicePicker = ({ value, onChange, multiple = false }: Props) => {
    const { palette } = useTheme();
    const navigate = useNavigate();
    const { t } = useTerminology();
    const organizationId = useAppSelector((store) => store.ui.activeOrganization);
    const currentOrganization = useAppSelector((store) => store.organizations[organizationId] ?? null);
    const usedBranch = useAppSelector((store) => store.order.branch)
    const adminOrderPath = location.pathname.includes(`/comercios/${organizationId}/pedidos`)

    const activeBranchServices = useMemo(() => {
        const OrgBranches = currentOrganization?.branches ?? [];
        const services = OrgBranches.find((branch: Branch) => branch.id === usedBranch?.id)?.services;
        const dineModes = DINING_MODES.filter((mode) => services?.includes(mode.id));
        return dineModes;
    }, [currentOrganization, usedBranch]);


    const servicesToRender = useMemo(() => {
        return adminOrderPath ? activeBranchServices : DINING_MODES;
    }, [adminOrderPath, activeBranchServices]);

    const handleToggle = (flowId: string) => {
        if (multiple) {
            const currentArray = Array.isArray(value) ? value : [];
            const currentIndex = currentArray?.indexOf(flowId);
            const newChecked = [...currentArray];

            if (currentIndex === -1) {
                newChecked.push(flowId);
            } else {
                newChecked.splice(currentIndex, 1);
            }

            onChange(newChecked);
        } else {
            onChange(flowId);
        }
    }

    return (
        <Paper sx={{ p: servicesToRender.length === 0 ? 3 : 0 }}>


            {servicesToRender.length === 0 ? (
                <EmptyState
                    title="No hay servicios habilitados"
                    description="No hay servicios habilitados para esta sucursal. Por favor, habilita al menos un servicio (Delivery o PickUp) en las configuraciones para poder crear pedidos."
                    action={{
                        variant: "contained",
                        color: "primary",
                        onClick: () => navigate(`/comercios/${organizationId}/configuraciones`),
                        children: "Ir a Configuraciones",
                    }}
                />
            ) : (
                <Stack spacing={1} padding={2}>
                    <Stack mb={1}>
                        <Typography variant="h3">Servicios Disponibles</Typography>
                        <Typography variant="body1" color="text.secondary">
                            {multiple ? "Seleccione los servicios que ofrece esta sucursal" : "Seleccione el servicio que ofrece esta sucursal"}
                        </Typography>
                    </Stack>
                    <List>
                        <Stack spacing={1}>
                            {servicesToRender.map((mode) => {
                                const isSelected = multiple ?
                                    Array.isArray(value) && value.includes(mode.id) :
                                    value === mode.id;

                                const secondaryText = mode.id === MENU_FLOWS.PICK_UP
                                    ? `Pides en la app y lo recoges ${t("atEstablishment")}`
                                    : mode.id === MENU_FLOWS.DINE_IN
                                        ? `Disfruta ${t("atEstablishment")}`
                                        : mode.description;

                                return (
                                    <ListItemButton
                                        key={mode.id}
                                        onClick={() => handleToggle(mode.id)}
                                        sx={{
                                            borderRadius: 1,
                                            mb: 0.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '&.Mui-selected': {
                                                backgroundColor: 'action.selected',
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            <HugeiconsIcon icon={mode.icon} size={32} color={palette.primary.main} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={mode.title}
                                            secondary={secondaryText}
                                        />
                                        <ListItemIcon sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 40 }}>
                                            {multiple ? (
                                                <Checkbox
                                                    edge="end"
                                                    checked={value?.indexOf(mode.id) !== -1}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            ) : (
                                                <Radio
                                                    edge="end"
                                                    checked={isSelected}
                                                    disableRipple
                                                />
                                            )}
                                        </ListItemIcon>
                                    </ListItemButton>
                                )
                            })}
                        </Stack>
                    </List>
                </Stack>
            )}
        </Paper>
    );
}

export default ServicePicker